import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import {
    createHandlerChainRunner,
    Handler,
    HasOnSuccess,
    markSkipOnCompleteIfOnSuccessIsSet,
} from "@insite/client-framework/HandlerCreator";
import { sendAddToCartEvent } from "@insite/client-framework/OdpEvents/UpdateCartEvents";
import { API_URL_CURRENT_FRAGMENT } from "@insite/client-framework/Services/ApiService";
import { AddCartLinesApiParameter, addLineCollection } from "@insite/client-framework/Services/CartService";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { CartLineCollectionModel, CartLineModel } from "@insite/client-framework/Types/ApiModels";
import { getOptimisticCartDelta } from "@thkStore/Common/Utilities/optimisticCartCount";

type Parameter = {
    productInfos: ProductInfo[];
} & HasOnSuccess<CartLineCollectionModel>;

type Props = {
    apiParameter: AddCartLinesApiParameter;
    apiResult: CartLineCollectionModel;
};

type HandlerType = Handler<Parameter, Props>;

export const DispatchBeginUpdatingCart: HandlerType = props => {
    props.dispatch({
        type: "Context/BeginUpdatingCart",
    });
};

export const PopulateApiParameter: HandlerType = props => {
    const cartLineCollection: CartLineModel[] = [];
    props.parameter.productInfos.forEach(o => {
        const cartLine = {
            productId: o.productId,
            qtyOrdered: o.qtyOrdered,
            unitOfMeasure: o.unitOfMeasure,
        } as CartLineModel;
        if ((o as any).properties) {
            cartLine.properties = (o as any).properties;
        }
        cartLineCollection.push(cartLine);
    });

    cartLineCollection.forEach(line => {
        const parsedQty = line.qtyOrdered ? parseFloat(line.qtyOrdered.toString()) : 1;
        line.qtyOrdered = parsedQty > 0 ? parsedQty : 1;
    });

    props.apiParameter = {
        cartLineCollection: {
            cartLines: cartLineCollection,
        },
        cartId: API_URL_CURRENT_FRAGMENT,
    } as AddCartLinesApiParameter;
};

export const SendDataToApi: HandlerType = async props => {
    props.apiResult = await addLineCollection(props.apiParameter);
};

// Optimistically bump the header cart badge the instant the add succeeds, so it doesn't wait
// for the follow-up full cart reload. The delta matches the "Mini-Cart Quantity Display" mode
// (inferred from the current cart) and is reset when the real cart finishes loading
// (see OptimisticCartCountReducer), so any over/undercount self-corrects within ~1s.
export const DispatchOptimisticCount: HandlerType = props => {
    const cart = getCurrentCartState(props.getState()).value;
    const delta = getOptimisticCartDelta(
        cart,
        props.parameter.productInfos.map(o => ({ productId: o.productId, qtyOrdered: o.qtyOrdered })),
    );
    if (delta > 0) {
        props.dispatch({
            type: "Cart/OptimisticCount/Add",
            delta,
        });
    }
};

export const DispatchCompleteUpdatingCart: HandlerType = props => {
    props.dispatch({
        type: "Context/CompleteUpdatingCart",
    });
};

export const SendAddToCartEvent: HandlerType = props => {
    props.parameter.productInfos.forEach(o => {
        sendAddToCartEvent(
            {
                productId: o.productId,
                qtyOrdered: o.qtyOrdered,
            },
            props.getState(),
        );
    });
};

export const ExecuteOnSuccessCallback: HandlerType = props => {
    markSkipOnCompleteIfOnSuccessIsSet(props);
    props.parameter.onSuccess?.(props.apiResult);
};

export const chain = [
    DispatchBeginUpdatingCart,
    PopulateApiParameter,
    SendDataToApi,
    DispatchOptimisticCount,
    DispatchCompleteUpdatingCart,
    SendAddToCartEvent,
    ExecuteOnSuccessCallback,
];

const addCartLines = createHandlerChainRunner(chain, "AddCartLines");
export default addCartLines;
